import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  Sparkles, Edit2, Save, X, Target, Loader2,
  Crown, CheckCircle2, ChevronRight, Scroll, Swords,
  MessageSquare, Layers, ArrowRight, Quote, AlertTriangle,
  Crosshair, Lightbulb
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RotateCcw } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useState, useEffect } from "react";
import type { MarketingStrategy } from "@shared/schema";
import { Link } from "wouter";
import { useIsHotelEdition } from "@/lib/hotelEdition";

// Hotel-Edition (PLAN §5.1 item 5, Mike A4): for hotels this page is the "LinkedIn-Strategie" (its pillars
// feed only LinkedIn; Instagram topics live in the Themenkatalog), with German section names.
const STRATEGY_TITLE = { hotel: "LinkedIn-Strategie", agency: "Marketingstrategie" } as const;
import type { OnboardingStatusResponse, StrategyRequestResponse } from "@/lib/strategyRefine";

// Simple markdown renderer for bold text and basic formatting
function renderMarkdown(text: string): JSX.Element[] {
  const elements: JSX.Element[] = [];
  const lines = text.split('\n');
  
  lines.forEach((line, lineIndex) => {
    // Process bold text (**text** or __text__)
    const parts = line.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
    const processedParts = parts.map((part, partIndex) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${lineIndex}-${partIndex}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('__') && part.endsWith('__')) {
        return <strong key={`${lineIndex}-${partIndex}`} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      }
      return <span key={`${lineIndex}-${partIndex}`}>{part}</span>;
    });
    
    elements.push(
      <span key={lineIndex}>
        {processedParts}
        {lineIndex < lines.length - 1 && <br />}
      </span>
    );
  });
  
  return elements;
}

interface StrategyResponse {
  strategyGeneratedAt: string | null;
  strategy: MarketingStrategy | null;
  company_id: string;
  generationTriggered: boolean;
  /** Hotels: the team writes the strategy. "Generieren" becomes a request to the team, no reset buttons. */
  strategyByTeam?: boolean;
  teamRequestedAt?: string | null;
  /** The hotel's last request to the team is still open (no new strategy since): show the 2-Werktage hint. */
  teamRequestOpen?: boolean;
}

interface ContentPillar {
  name: string;
  type: "context_setting" | "capability_building" | "solution_supporting";
  themes: string[];
  primaryBuyerStage: string;
  why?: string;
}

interface StrategyMessage {
  title: string;
  before: string;
  bridge: string;
  after: string;
}

export default function MarketingStrategyPage() {
  const { data, isLoading } = useQuery<StrategyResponse>({
    queryKey: ['/api/strategy'],
    refetchInterval: (query) => {
      const data = query.state.data as StrategyResponse | undefined;
      // Poll every 5 seconds while a strategy generation is in flight and no strategy exists yet
      if (!data?.strategy && data?.generationTriggered) {
        return 5000;
      }
      return false;
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" data-testid="loader-strategy" />
      </div>
    );
  }

  // Show combined ReadyToGenerateState when no strategy exists yet
  if (!data?.strategy) {
    return (
      <ReadyToGenerateState
        generationTriggered={data?.generationTriggered || false}
        byTeam={data?.strategyByTeam === true}
        teamRequestOpen={data?.teamRequestOpen === true}
      />
    );
  }

  return (
    <DashboardState strategy={data.strategy} byTeam={data.strategyByTeam === true} teamRequestOpen={data.teamRequestOpen === true} />
  );
}

interface ReadyToGenerateStateProps {
  generationTriggered: boolean;
  byTeam: boolean;
  teamRequestOpen: boolean;
}

/** Shown to a hotel after it asked the team for its strategy (Sie-Form, no dashes; same text as the server). */
const TEAM_REQUEST_MESSAGE = "Ihr Team überarbeitet Ihre Strategie, Sie erhalten sie innerhalb von 2 Werktagen.";

function ReadyToGenerateState({ generationTriggered: serverTriggered, byTeam, teamRequestOpen }: ReadyToGenerateStateProps) {
  const isHotel = useIsHotelEdition();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  // A hotel never waits on the engine: its request goes to the team, the page shows the hint instead.
  const generationTriggered = serverTriggered && !byTeam;
  // Show loading if generation was triggered (server-side flag) or after local submission
  const [isWaitingForGeneration, setIsWaitingForGeneration] = useState(generationTriggered);
  const [customerMainProblem, setCustomerMainProblem] = useState("");
  const [customerEnemy, setCustomerEnemy] = useState("");
  const [contrarianPov, setContrarianPov] = useState("");

  // Sync local state with server-side generationTriggered when it changes (e.g., after reset)
  useEffect(() => {
    setIsWaitingForGeneration(generationTriggered);
  }, [generationTriggered]);
  
  const generateMutation = useMutation({
    mutationFn: async (data: { customerMainProblem: string; customerEnemy: string; contrarianPov: string }) => {
      return await apiRequest("POST", "/api/strategy/trigger-generation", data);
    },
    onSuccess: (res: StrategyRequestResponse) => {
      if (res?.teamRequest) {
        toast({ title: "Anfrage an Ihr Team gesendet", description: TEAM_REQUEST_MESSAGE });
      } else {
        toast({
          title: "Strategie wird erstellt",
          description: "Ihr A-Team arbeitet jetzt an Ihrer Marketingstrategie. Das kann einige Minuten dauern.",
        });
      }
      setShowModal(false);
      setIsWaitingForGeneration(!res?.teamRequest);
      setCustomerMainProblem("");
      setCustomerEnemy("");
      setContrarianPov("");
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
    },
    onError: (err: any) => {
      toast({
        title: "Fehler",
        // Hotel request whose mail failed: the server says the answers are saved and to try again later.
        description: err?.data?.teamRequest && err?.data?.error
          ? err.data.error
          : "Die Strategie konnte nicht gestartet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/strategy/reset", {});
    },
    onSuccess: (data: any) => {
      toast({
        title: "Zurückgesetzt",
        description: data.message || "Sie können es jetzt erneut versuchen.",
      });
      setIsWaitingForGeneration(false);
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Reset fehlgeschlagen. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!customerMainProblem.trim() || !customerEnemy.trim() || !contrarianPov.trim()) {
      toast({
        title: "Bitte alle Felder ausfüllen",
        description: "Wir brauchen alle drei Antworten, um Ihre Strategie zu erstellen.",
        variant: "destructive",
      });
      return;
    }
    generateMutation.mutate({
      customerMainProblem: customerMainProblem.trim(),
      customerEnemy: customerEnemy.trim(),
      contrarianPov: contrarianPov.trim(),
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="flex items-center space-x-2">
        <Target className="h-6 w-6 text-primary" />
        <h1 className="text-3xl font-bold font-playfair">{STRATEGY_TITLE[isHotel ? "hotel" : "agency"]}</h1>
      </div>

      <Card className="border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
        <CardContent className="pt-8 pb-8">
          <div className="flex flex-col lg:flex-row gap-8 items-center">
            <div className="flex-shrink-0">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-12 w-12 text-primary" />
              </div>
            </div>
            <div className="flex-1 text-center lg:text-left">
              <h2 className="text-2xl font-bold mb-3">
                Ihre KI-gestützte Marketingstrategie
              </h2>
              <p className="text-muted-foreground mb-6 max-w-2xl">
                Lassen Sie Ihr A-Team eine maßgeschneiderte Marketingstrategie für Ihr Hotel entwickeln. 
                Auf Grundlage Ihres Profils erstellen wir eine fundierte Strategie mit Content-Säulen, 
                die Ihre Gäste genau dort abholt, wo sie stehen.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background border">
                  <Crosshair className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">{isHotel ? "Strategische Grundlage" : "Strategic Foundation"}</h4>
                    <p className="text-xs text-muted-foreground">Positionierung & POV</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background border">
                  <Lightbulb className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">{isHotel ? "Content-Säulen" : "Content Pillars"}</h4>
                    <p className="text-xs text-muted-foreground">3 themenbezogene Säulen</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-background border">
                  <AlertTriangle className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-sm">{isHotel ? "Problem-Landkarte" : "Problem Stack"}</h4>
                    <p className="text-xs text-muted-foreground">Pain Points & Auswirkungen</p>
                  </div>
                </div>
              </div>

              {isWaitingForGeneration ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Strategie wird generiert... Die Seite aktualisiert sich automatisch.
                  </p>
                  {!byTeam && <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => resetMutation.mutate()}
                    disabled={resetMutation.isPending}
                    className="mt-2"
                    data-testid="button-reset-strategy"
                  >
                    {resetMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Wird zurückgesetzt...
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 mr-2" />
                        Abbrechen & neu starten
                      </>
                    )}
                  </Button>}
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4 lg:items-start">
                  {byTeam && teamRequestOpen && (
                    <p
                      role="status"
                      className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900"
                      data-testid="text-strategy-team-requested"
                    >
                      {TEAM_REQUEST_MESSAGE}
                    </p>
                  )}
                  <Button 
                    size="lg" 
                    className="gap-2" 
                    data-testid="button-generate-strategy"
                    onClick={() => setShowModal(true)}
                  >
                    <Sparkles className="h-4 w-4" />
                    {byTeam ? "Strategie beim Team anfragen" : "Strategie generieren"}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Strategy Generation Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {byTeam ? "Strategie beim Team anfragen" : "Ihre Marketingstrategie starten"}
            </DialogTitle>
            <DialogDescription>
              Beantworten Sie diese drei Fragen, damit Ihr A-Team eine maßgeschneiderte Strategie für Sie erstellen kann.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="customer-main-problem" className="text-base font-semibold">
                Was beschäftigt Ihre Gäste, bevor sie bei Ihnen buchen?
              </Label>
              <p className="text-sm text-muted-foreground">
                Welchen Wunsch oder welches Problem haben Ihre Gäste, wenn sie ein Hotel suchen?
              </p>
              <Textarea
                id="customer-main-problem"
                placeholder="z. B. Sie wollen endlich abschalten, finden aber nur austauschbare Häuser ohne Charakter."
                value={customerMainProblem}
                onChange={(e) => setCustomerMainProblem(e.target.value)}
                className="min-h-[100px]"
                data-testid="textarea-customer-main-problem"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="customer-enemy" className="text-base font-semibold">
                Wogegen stehen Sie gemeinsam mit Ihren Gästen?
              </Label>
              <p className="text-sm text-muted-foreground">
                Was steht einem gelungenen Aufenthalt im Weg? Das kann eine Gewohnheit, ein Trend oder ein Ärgernis der Branche sein.
              </p>
              <Textarea
                id="customer-enemy"
                placeholder="z. B. Massenabfertigung in großen Bettenburgen, Urlaub nach Checkliste, ständige Erreichbarkeit."
                value={customerEnemy}
                onChange={(e) => setCustomerEnemy(e.target.value)}
                className="min-h-[100px]"
                data-testid="textarea-customer-enemy"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contrarian-pov" className="text-base font-semibold">
                Was sehen Sie anders als die meisten Hotels?
              </Label>
              <p className="text-sm text-muted-foreground">
                Ihre Überzeugung, mit der Sie sich von anderen Häusern abheben.
              </p>
              <Textarea
                id="contrarian-pov"
                placeholder="z. B. Erholung braucht keinen großen Wellnessbereich. Ruhe, gutes Essen und herzliche Gastgeber reichen."
                value={contrarianPov}
                onChange={(e) => setContrarianPov(e.target.value)}
                className="min-h-[100px]"
                data-testid="textarea-contrarian-pov"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowModal(false)}
              data-testid="button-cancel-generate"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={generateMutation.isPending}
              className="gap-2"
              data-testid="button-submit-generate"
            >
              {generateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  {byTeam ? "Anfrage senden" : "Strategie erstellen lassen"}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface DashboardStateProps {
  strategy: MarketingStrategy;
  byTeam: boolean;
  teamRequestOpen: boolean;
}

function DashboardState({ strategy, byTeam, teamRequestOpen }: DashboardStateProps) {
  const isHotel = useIsHotelEdition();
  const { toast } = useToast();
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  // The automatic first strategy is preliminary until the hotel answers the foundation questions.
  const { data: onboardingStatus } = useQuery<OnboardingStatusResponse>({ queryKey: ["/api/onboarding/status"], retry: false });
  const strategyStatus = onboardingStatus?.strategy;
  const preliminary = strategyStatus?.state === "ready" && strategyStatus.refinement === "preliminary";
  const refining = preliminary && strategyStatus?.state === "ready" && strategyStatus.refining;

  const fullResetMutation = useMutation({
    mutationFn: async () => {
      return await apiRequest("POST", "/api/strategy/full-reset", {});
    },
    onSuccess: () => {
      toast({
        title: "Strategie zurückgesetzt",
        description: "Sie können die drei Fragen jetzt erneut beantworten.",
      });
      setShowResetConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
    },
    onError: () => {
      toast({
        title: "Fehler",
        description: "Reset fehlgeschlagen. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="container mx-auto p-6 space-y-12">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <Target className="h-6 w-6 text-primary" />
            <h1 className="text-3xl font-bold font-playfair">{STRATEGY_TITLE[isHotel ? "hotel" : "agency"]}</h1>
          </div>
          {preliminary && (
            <p className="mt-1 text-sm text-muted-foreground" data-testid="text-strategy-preliminary">
              <Badge variant="secondary" className="mr-2">Erste vorläufige Strategie</Badge>
              {refining ? (
                "Ihre Strategie wird gerade geschärft."
              ) : (
                <Link href="/a-team-briefing#strategie-schaerfen" className="underline hover:text-foreground">
                  Strategie schärfen
                </Link>
              )}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Hotels: no reset, the strategy is the team's (Mike 2026-10-08); the server refuses it too. */}
          {!byTeam && <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="button-reset-strategy">
                <RotateCcw className="w-4 h-4 mr-1" />
                Neu starten
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Strategie komplett zurücksetzen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Ihre gesamte Marketingstrategie wird gelöscht, und Sie können die drei Fragen erneut beantworten. Diese Aktion kann nicht rückgängig gemacht werden.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => fullResetMutation.mutate()}
                  disabled={fullResetMutation.isPending}
                  data-testid="button-confirm-reset"
                >
                  {fullResetMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Wird zurückgesetzt...
                    </>
                  ) : (
                    "Ja, zurücksetzen"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>}
          <Badge variant="default" className="bg-green-500">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Generiert
          </Badge>
        </div>
      </div>

      {byTeam && teamRequestOpen && (
        <p
          role="status"
          className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900"
          data-testid="text-strategy-team-requested"
        >
          {TEAM_REQUEST_MESSAGE}
        </p>
      )}

      {/* Section 1: Manifesto / Brand Story */}
      <ManifestoSection strategy={strategy} />

      {/* Section 2: The Enemy */}
      <EnemySection strategy={strategy} />

      {/* Section 3: Strategic Positioning (POV & Positioning) */}
      <PositioningSection strategy={strategy} />

      {/* Section 4: Core Messages (Before/Bridge/After) */}
      <MessagesSection strategy={strategy} />

      {/* Section 4: Content Pillars */}
      <ContentPillarsSection strategy={strategy} />

      {/* Section 5: Problem Stack */}
      <ProblemStackSection strategy={strategy} />
    </div>
  );
}

// Helper to strip markdown header markers from titles (## 3. THE ENEMY -> 3. THE ENEMY)
function stripMarkdownHeaders(text: string): string {
  if (!text) return text;
  return text.replace(/^#{1,6}\s+/, '').trim();
}

// Helper to parse subsections within content (bold titles like **Die Welt wie sie ist**)
interface ManifestoSubsection {
  title: string;
  content: string;
}

interface ManifestoSectionData {
  title: string;
  subsections: ManifestoSubsection[];
  rawContent: string;
}

function parseManifestoWithSubsections(manifesto: string): ManifestoSectionData[] {
  if (!manifesto) return [];
  
  const lines = manifesto.split('\n');
  const sections: ManifestoSectionData[] = [];
  let currentSection: ManifestoSectionData | null = null;
  let currentSubsection: ManifestoSubsection | null = null;
  let contentLines: string[] = [];
  let rawContentLines: string[] = [];
  
  for (const line of lines) {
    // Match Markdown headings (## or ###)
    const headingMatch = line.match(/^#{2,3}\s+(.+)$/);
    // Match bold subsection titles like **Title** or **Title:**
    const boldTitleMatch = line.match(/^\*\*(.+?)\*\*:?\s*$/);
    
    if (headingMatch) {
      // Save previous subsection
      if (currentSubsection && currentSection) {
        currentSubsection.content = contentLines.join('\n').trim();
        if (currentSubsection.title || currentSubsection.content) {
          currentSection.subsections.push(currentSubsection);
        }
      }
      // Save previous section
      if (currentSection) {
        currentSection.rawContent = rawContentLines.join('\n').trim();
        if (currentSection.title || currentSection.subsections.length > 0) {
          sections.push(currentSection);
        }
      }
      // Start new section
      currentSection = { title: stripMarkdownHeaders(headingMatch[1].trim()), subsections: [], rawContent: '' };
      currentSubsection = null;
      contentLines = [];
      rawContentLines = [];
    } else if (boldTitleMatch && currentSection) {
      // Save previous subsection
      if (currentSubsection) {
        currentSubsection.content = contentLines.join('\n').trim();
        if (currentSubsection.title || currentSubsection.content) {
          currentSection.subsections.push(currentSubsection);
        }
      }
      // Start new subsection
      currentSubsection = { title: boldTitleMatch[1].trim(), content: '' };
      contentLines = [];
      rawContentLines.push(line);
    } else if (currentSubsection) {
      contentLines.push(line);
      rawContentLines.push(line);
    } else if (currentSection) {
      rawContentLines.push(line);
      // Content before first subsection - add to first implicit subsection
      if (line.trim()) {
        if (!currentSubsection) {
          currentSubsection = { title: '', content: '' };
        }
        contentLines.push(line);
      }
    } else {
      // Content before first heading - create Manifesto section
      if (line.trim()) {
        if (!currentSection) {
          currentSection = { title: 'Manifesto', subsections: [], rawContent: '' };
        }
        rawContentLines.push(line);
        // Check if this line is a bold title
        if (boldTitleMatch) {
          currentSubsection = { title: boldTitleMatch[1].trim(), content: '' };
          contentLines = [];
        } else if (currentSubsection) {
          contentLines.push(line);
        } else {
          // Start with content before any subsection
          currentSubsection = { title: '', content: '' };
          contentLines.push(line);
        }
      }
    }
  }
  
  // Save last subsection
  if (currentSubsection && currentSection) {
    currentSubsection.content = contentLines.join('\n').trim();
    if (currentSubsection.title || currentSubsection.content) {
      currentSection.subsections.push(currentSubsection);
    }
  }
  
  // Save last section
  if (currentSection) {
    currentSection.rawContent = rawContentLines.join('\n').trim();
    if (currentSection.title || currentSection.subsections.length > 0) {
      sections.push(currentSection);
    }
  }
  
  return sections;
}

// Section 1: Manifesto / Brand Story - With nested subsection accordions
function ManifestoSection({ strategy }: { strategy: MarketingStrategy }) {
  const isHotel = useIsHotelEdition();
  const manifesto = strategy.manifesto;
  const sections = parseManifestoWithSubsections(manifesto || '');
  
  // Find the main Manifesto section (first section or one titled "Manifesto")
  const manifestoSection = sections.find(s => s.title === 'Manifesto') || sections[0];
  const otherSections = sections.filter(s => s !== manifestoSection);
  
  return (
    <section className="space-y-6" data-testid="section-manifesto">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Scroll className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-2xl font-bold font-playfair">{isHotel ? "Ihre Markengeschichte" : "Ihre Brand Story"}</h2>
          <p className="text-sm text-muted-foreground">Das Manifest Ihrer Marke</p>
        </div>
      </div>
      
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary/5 overflow-hidden">
        <CardContent className="p-6">
          {manifestoSection && manifestoSection.subsections.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 pb-2 border-b border-primary/10">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center text-sm font-bold text-primary shadow-inner">
                  <Scroll className="h-4 w-4" />
                </div>
                <span className="font-semibold text-lg text-primary">Manifesto</span>
              </div>
              
              {/* Render any intro content without a title (before first subsection) */}
              {manifestoSection.subsections.filter((sub: ManifestoSubsection) => !sub.title && sub.content).map((introSub: ManifestoSubsection, idx: number) => (
                <div key={`intro-${idx}`} className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {renderMarkdown(introSub.content)}
                </div>
              ))}
              
              {/* Render titled subsections as collapsible accordions */}
              {manifestoSection.subsections.some((sub: ManifestoSubsection) => sub.title) && (
                <Accordion type="single" collapsible className="w-full space-y-2">
                  {manifestoSection.subsections.filter((sub: ManifestoSubsection) => sub.title).map((subsection: ManifestoSubsection, subIndex: number) => (
                    <AccordionItem 
                      key={subIndex} 
                      value={`subsection-${subIndex}`}
                      className="border border-primary/15 rounded-lg bg-background/60 overflow-hidden"
                    >
                      <AccordionTrigger className="hover:no-underline px-4 py-3 hover:bg-primary/5 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-6 h-6 rounded-full bg-primary/15 flex items-center justify-center text-xs font-semibold text-primary">
                            {subIndex + 1}
                          </div>
                          <span className="font-medium text-sm text-left">{subsection.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="pl-9 border-l-2 border-primary/15">
                          <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap pl-3">
                            {renderMarkdown(subsection.content)}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </div>
          ) : manifesto ? (
            <div className="prose prose-lg dark:prose-invert max-w-none">
              <blockquote className="border-l-4 border-primary pl-6 italic text-lg leading-relaxed whitespace-pre-wrap">
                {manifesto}
              </blockquote>
            </div>
          ) : (
            <div className="text-center py-8">
              <Quote className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground italic">
                Ihr Manifest wird von der KI erstellt und erscheint hier.
              </p>
            </div>
          )}
          
          {/* Render other sections (non-Manifesto) as simple content blocks */}
          {otherSections.map((section: ManifestoSectionData, sectionIndex: number) => (
            <div key={sectionIndex} className="mt-6 pt-6 border-t border-primary/10">
              <h3 className="font-semibold text-base mb-3 text-foreground">{stripMarkdownHeaders(section.title)}</h3>
              <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-wrap">
                {renderMarkdown(section.rawContent)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

// Section 2: The Enemy
function EnemySection({ strategy }: { strategy: MarketingStrategy }) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tempValues, setTempValues] = useState({
    enemyName: strategy.enemyName || "",
    enemyDescription: strategy.enemyDescription || "",
  });
  const [originalValues, setOriginalValues] = useState({
    enemyName: strategy.enemyName || "",
    enemyDescription: strategy.enemyDescription || "",
  });

  useEffect(() => {
    const newValues = {
      enemyName: strategy.enemyName || "",
      enemyDescription: strategy.enemyDescription || "",
    };
    setTempValues(newValues);
    setOriginalValues(newValues);
  }, [strategy]);

  const hasUnsavedChanges = isEditing && (
    tempValues.enemyName !== originalValues.enemyName ||
    tempValues.enemyDescription !== originalValues.enemyDescription
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const confirmDiscard = () => {
    setTempValues(originalValues);
    setIsEditing(false);
    setShowDiscardDialog(false);
    toast({ title: "Verworfen", description: "Änderungen wurden verworfen." });
  };

  const updateMutation = useMutation({
    mutationFn: async (data: typeof tempValues) => {
      return await apiRequest("PUT", "/api/strategy", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
      toast({ title: "Gespeichert", description: "Änderungen wurden gespeichert." });
      setIsEditing(false);
    },
    onError: () => {
      toast({ title: "Fehler", description: "Speichern fehlgeschlagen.", variant: "destructive" });
    },
  });

  return (
    <section className="space-y-6" data-testid="section-enemy">
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ungespeicherte Änderungen</AlertDialogTitle>
            <AlertDialogDescription>
              Sie haben Änderungen vorgenommen, die noch nicht gespeichert wurden. Möchten Sie diese wirklich verwerfen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>Verwerfen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
            <Swords className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-playfair">Ihr Gegner</h2>
            <p className="text-sm text-muted-foreground">Das Problem, gegen das Sie antreten</p>
          </div>
        </div>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="button-edit-enemy">
            <Edit2 className="h-4 w-4 mr-1" />
            Bearbeiten
          </Button>
        )}
      </div>

      <Card className="border-l-4 border-l-red-500">
        <CardContent className="p-6">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-semibold">Name des Gegners</Label>
                <Textarea
                  value={tempValues.enemyName}
                  onChange={(e) => setTempValues(prev => ({ ...prev, enemyName: e.target.value }))}
                  className="mt-2"
                  placeholder="z.B. Das Content-Hamsterrad"
                />
              </div>
              <div>
                <Label className="text-sm font-semibold">Beschreibung</Label>
                <Textarea
                  value={tempValues.enemyDescription}
                  onChange={(e) => setTempValues(prev => ({ ...prev, enemyDescription: e.target.value }))}
                  className="mt-2 min-h-[120px]"
                  placeholder="Warum ist das ein Problem? Wie gehen Sie dagegen vor?"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  <X className="h-4 w-4 mr-1" />
                  Abbrechen
                </Button>
                <Button size="sm" onClick={() => updateMutation.mutate(tempValues)} disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                  Speichern
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold text-red-600 dark:text-red-400">
                  {strategy.enemyName ? stripMarkdownHeaders(strategy.enemyName) : <span className="text-muted-foreground italic">Noch nicht definiert</span>}
                </h3>
                <Badge variant="outline" className="text-xs text-primary border-primary/30">KI-generiert</Badge>
              </div>
              {strategy.enemyDescription && (
                <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                  {renderMarkdown(stripMarkdownHeaders(strategy.enemyDescription))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// Section 3: Strategic Positioning (POV & Positioning Statement)
function PositioningSection({ strategy }: { strategy: MarketingStrategy }) {
  const isHotel = useIsHotelEdition();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tempValues, setTempValues] = useState({
    povStatement: strategy.povStatement || "",
    positioningStatement: strategy.positioningStatement || "",
  });
  const [originalValues, setOriginalValues] = useState({
    povStatement: strategy.povStatement || "",
    positioningStatement: strategy.positioningStatement || "",
  });

  useEffect(() => {
    const newValues = {
      povStatement: strategy.povStatement || "",
      positioningStatement: strategy.positioningStatement || "",
    };
    setTempValues(newValues);
    setOriginalValues(newValues);
  }, [strategy]);

  const hasUnsavedChanges = isEditing && (
    tempValues.povStatement !== originalValues.povStatement ||
    tempValues.positioningStatement !== originalValues.positioningStatement
  );

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowDiscardDialog(true);
    } else {
      setIsEditing(false);
    }
  };

  const confirmDiscard = () => {
    setTempValues(originalValues);
    setIsEditing(false);
    setShowDiscardDialog(false);
    toast({ title: "Verworfen", description: "Änderungen wurden verworfen." });
  };

  const updateMutation = useMutation({
    mutationFn: async (data: typeof tempValues) => {
      return await apiRequest("PUT", "/api/strategy", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
      toast({ title: "Gespeichert", description: "Änderungen wurden gespeichert." });
      setIsEditing(false);
    },
    onError: () => {
      toast({ title: "Fehler", description: "Speichern fehlgeschlagen.", variant: "destructive" });
    },
  });

  return (
    <section className="space-y-6" data-testid="section-positioning">
      <AlertDialog open={showDiscardDialog} onOpenChange={setShowDiscardDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ungespeicherte Änderungen</AlertDialogTitle>
            <AlertDialogDescription>
              Sie haben Änderungen vorgenommen, die noch nicht gespeichert wurden. Möchten Sie diese wirklich verwerfen?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zurück</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard}>Verwerfen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Crosshair className="h-5 w-5 text-blue-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-playfair">Strategische Positionierung</h2>
            <p className="text-sm text-muted-foreground">Ihre eigene Sichtweise und Marktposition</p>
          </div>
        </div>
        {!isEditing && (
          <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="button-edit-positioning">
            <Edit2 className="h-4 w-4 mr-1" />
            Bearbeiten
          </Button>
        )}
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* POV Statement Card */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-blue-500" />
              <CardTitle className="text-lg">{isHotel ? "Ihr Standpunkt" : "Point of View"}</CardTitle>
            </div>
            <CardDescription>Ihre eigene Sichtweise auf den Markt</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={tempValues.povStatement}
                onChange={(e) => setTempValues(prev => ({ ...prev, povStatement: e.target.value }))}
                className="min-h-[120px]"
                placeholder="z.B. Während andere auf Quantität setzen, glauben wir..."
              />
            ) : (
              <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {strategy.povStatement ? renderMarkdown(strategy.povStatement) : <span className="italic">Noch nicht definiert</span>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Positioning Statement Card */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              <CardTitle className="text-lg">Positionierung</CardTitle>
            </div>
            <CardDescription>Wie Sie sich im Markt positionieren</CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <Textarea
                value={tempValues.positioningStatement}
                onChange={(e) => setTempValues(prev => ({ ...prev, positioningStatement: e.target.value }))}
                className="min-h-[120px]"
                placeholder="Für [Zielgruppe] die [Problem] haben, bieten wir..."
              />
            ) : (
              <div className="text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {strategy.positioningStatement ? renderMarkdown(strategy.positioningStatement) : <span className="italic">Noch nicht definiert</span>}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isEditing && (
        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={handleCancel}>
            <X className="h-4 w-4 mr-1" />
            Abbrechen
          </Button>
          <Button size="sm" onClick={() => updateMutation.mutate(tempValues)} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Speichern
          </Button>
        </div>
      )}
    </section>
  );
}

// Helper function to safely parse messages array
function parseMessages(rawMessages: unknown): StrategyMessage[] {
  if (!Array.isArray(rawMessages)) return [];
  return rawMessages.filter((msg): msg is StrategyMessage => 
    typeof msg === 'object' && msg !== null &&
    typeof (msg as any).title === 'string' &&
    typeof (msg as any).before === 'string' &&
    typeof (msg as any).bridge === 'string' &&
    typeof (msg as any).after === 'string'
  );
}

// Section 3: Core Messages (Before/Bridge/After)
function MessagesSection({ strategy }: { strategy: MarketingStrategy }) {
  const messages = parseMessages(strategy.messages);

  return (
    <section className="space-y-6" data-testid="section-messages">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
          <MessageSquare className="h-5 w-5 text-blue-500" />
        </div>
        <div>
          <h2 className="text-2xl font-bold font-playfair">Ihre 3 Kern-Botschaften</h2>
          <p className="text-sm text-muted-foreground">Transformation Stories für verschiedene Kundentypen</p>
        </div>
      </div>

      {messages.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-3">
          {messages.map((message, index) => (
            <Card key={index} className="relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-primary to-green-500" />
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{message.title || `Botschaft ${index + 1}`}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <span className="text-xs font-bold text-red-600">1</span>
                    </div>
                    <span className="text-xs font-semibold text-red-600 uppercase">Vorher</span>
                  </div>
                  <p className="text-sm text-muted-foreground pl-8">{message.before}</p>
                </div>
                
                <div className="flex justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                      <span className="text-xs font-bold text-yellow-600">2</span>
                    </div>
                    <span className="text-xs font-semibold text-yellow-600 uppercase">Brücke</span>
                  </div>
                  <p className="text-sm text-muted-foreground pl-8">{message.bridge}</p>
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <span className="text-xs font-bold text-green-600">3</span>
                    </div>
                    <span className="text-xs font-semibold text-green-600 uppercase">Nachher</span>
                  </div>
                  <p className="text-sm text-muted-foreground pl-8">{message.after}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <MessageSquare className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground italic">
              Ihre Kern-Botschaften werden von der KI erstellt.
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

// Section 4: Content Pillars
function ContentPillarsSection({ strategy }: { strategy: MarketingStrategy }) {
  const isHotel = useIsHotelEdition();
  const pillars = (strategy.contentPillars as ContentPillar[]) || [];

  const getPillarTypeLabel = (type: string) => {
    switch (type) {
      case "context_setting": return "Context Setting";
      case "capability_building": return "Capability Building";
      case "solution_supporting": return "Solution Supporting";
      default: return type;
    }
  };

  const getPillarTypeColor = (type: string) => {
    switch (type) {
      case "context_setting": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
      case "capability_building": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
      case "solution_supporting": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <section className="space-y-6" data-testid="section-pillars">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-purple-500/10 flex items-center justify-center">
          <Layers className="h-5 w-5 text-purple-500" />
        </div>
        <div>
          <h2 className="text-2xl font-bold font-playfair">Ihre Content-Säulen</h2>
          <p className="text-sm text-muted-foreground">Die 3 Themen-Säulen für Ihren Content</p>
        </div>
      </div>

      {pillars.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-3">
          {pillars.map((pillar, index) => (
            <Card key={index} className="relative overflow-hidden hover-elevate">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Badge className={getPillarTypeColor(pillar.type)}>
                    {getPillarTypeLabel(pillar.type)}
                  </Badge>
                  <Badge variant="outline" className="text-xs">
                    Säule {index + 1}
                  </Badge>
                </div>
                <CardTitle className="text-lg">{stripMarkdownHeaders(pillar.name)}</CardTitle>
                <CardDescription>
                  {isHotel ? "Phase" : "Buyer Stage"}: {pillar.primaryBuyerStage}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {pillar.why && (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Warum dieses Thema?</p>
                    <p className="text-sm">{pillar.why}</p>
                  </div>
                )}
                {/* A usable strategy only guarantees named pillars (isUsableStrategy), themes may be missing. */}
                {Array.isArray(pillar.themes) && pillar.themes.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Themen:</h4>
                    <div className="flex flex-wrap gap-2">
                      {pillar.themes.map((theme, themeIndex) => (
                        <Badge key={themeIndex} variant="secondary" className="text-xs">
                          {theme}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <Layers className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground italic">
              Ihre Content-Säulen werden von der KI erstellt.
            </p>
          </CardContent>
        </Card>
      )}
    </section>
  );
}

// Section 5: Problem Stack (Visual Funnel/Pyramid)
function ProblemStackSection({ strategy }: { strategy: MarketingStrategy }) {
  const isHotel = useIsHotelEdition();
  const { toast } = useToast();
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValues, setTempValues] = useState({
    surfaceProblem: strategy.surfaceProblem || "",
    rootCause: strategy.rootCause || "",
    businessImpact: strategy.businessImpact || "",
    emotionalImpact: strategy.emotionalImpact || "",
  });

  useEffect(() => {
    setTempValues({
      surfaceProblem: strategy.surfaceProblem || "",
      rootCause: strategy.rootCause || "",
      businessImpact: strategy.businessImpact || "",
      emotionalImpact: strategy.emotionalImpact || "",
    });
  }, [strategy]);

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<typeof tempValues>) => {
      return await apiRequest("PUT", "/api/strategy", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/strategy'] });
      toast({ title: "Gespeichert", description: "Änderungen wurden gespeichert." });
      setEditingField(null);
    },
    onError: () => {
      toast({ title: "Fehler", description: "Speichern fehlgeschlagen.", variant: "destructive" });
    },
  });

  const problemLevels = [
    { 
      field: "surfaceProblem" as const, 
      title: "Oberflächliches Problem", 
      description: "Was der Kunde als erstes wahrnimmt",
      color: "from-blue-500/20 to-blue-500/5",
      borderColor: "border-l-blue-500",
      width: "100%"
    },
    { 
      field: "rootCause" as const, 
      title: "Grundursache", 
      description: "Die tiefere Ursache des Problems",
      color: "from-orange-500/20 to-orange-500/5",
      borderColor: "border-l-orange-500",
      width: "85%"
    },
    { 
      field: "businessImpact" as const, 
      title: isHotel ? "Auswirkung auf Ihr Haus" : "Business Impact", 
      description: "Auswirkungen auf das Geschäft",
      color: "from-red-500/20 to-red-500/5",
      borderColor: "border-l-red-500",
      width: "70%"
    },
    { 
      field: "emotionalImpact" as const, 
      title: isHotel ? "Emotionale Wirkung" : "Emotionaler Impact", 
      description: "Wie sich das Problem anfühlt",
      color: "from-purple-500/20 to-purple-500/5",
      borderColor: "border-l-purple-500",
      width: "55%"
    },
  ];

  return (
    <section className="space-y-6" data-testid="section-problem-stack">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
          <AlertTriangle className="h-5 w-5 text-orange-500" />
        </div>
        <div>
          <h2 className="text-2xl font-bold font-playfair">Das Problem, das Sie lösen</h2>
          <p className="text-sm text-muted-foreground">Von der Oberfläche bis zur Emotion</p>
        </div>
      </div>

      <div className="space-y-3">
        {problemLevels.map(({ field, title, description, color, borderColor, width }) => (
          <div key={field} className="flex justify-center">
            <Card 
              className={`${borderColor} border-l-4 bg-gradient-to-r ${color} transition-all duration-300`}
              style={{ width }}
            >
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{title}</CardTitle>
                    <CardDescription className="text-xs">{description}</CardDescription>
                  </div>
                  {editingField !== field && (
                    <Button variant="ghost" size="sm" onClick={() => setEditingField(field)} data-testid={`button-edit-${field}`}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                {editingField === field ? (
                  <div className="space-y-3">
                    <Textarea
                      value={tempValues[field]}
                      onChange={(e) => setTempValues(prev => ({ ...prev, [field]: e.target.value }))}
                      className="min-h-20"
                      data-testid={`textarea-${field}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => setEditingField(null)} disabled={updateMutation.isPending}>
                        <X className="h-4 w-4 mr-1" />
                        Abbrechen
                      </Button>
                      <Button size="sm" onClick={() => updateMutation.mutate({ [field]: tempValues[field] })} disabled={updateMutation.isPending}>
                        {updateMutation.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                        Speichern
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {tempValues[field] ? renderMarkdown(tempValues[field]) : <span className="italic">Noch nicht ausgefüllt</span>}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </section>
  );
}

